import { Button, useToggle } from '@commenda/kakashi';
import { THumanReviewStepDefinition } from '@commenda/types';
import { TaskParty, TaskStatus } from '@prisma/client';
import { useMemo } from 'react';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { ServiceRequestTaskLabel } from '../../labels';
import { HumanReviewModal } from '../../modals/HumanReviewModal';
import { ServiceRequestTaskIconConfig } from '../../taskIconConfig';
import { isCurrentTimelineTask } from '../../../utils';
import MarkAsCompleteTask from '../MarkAsCompleteTask';
import { getCompletedTaskLabel } from '../getCompletedTaskLabel';

export const WorkflowHumanReviewTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { workflowExecution } = context;
  if (!workflowExecution?.WorkflowTemplateVersion?.definition)
    console.error('Workflow definition not found');
  if (!workflowExecution?.runtime?.stepExecutions)
    console.error('Workflow runtime step executions not found');

  const reviewModalVisibility = useToggle(false);

  const stepDefinition = useMemo(() => {
    if (!workflowExecution?.WorkflowTemplateVersion?.definition) return null;
    return workflowExecution.WorkflowTemplateVersion.definition.steps.find(
      (step) => step.id === task.workflowDefinitionStepId
    ) as THumanReviewStepDefinition | undefined;
  }, [workflowExecution, task.workflowDefinitionStepId]);

  if (!workflowExecution || !stepDefinition) {
    return (
      <MarkAsCompleteTask
        task={task}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
    );
  }

  const { role } = context;
  const isCurrentTask = isCurrentTimelineTask(task);
  const completedLabel = getCompletedTaskLabel({
    task,
    serviceRequest: context.serviceRequest,
  });

  const startButton = (label: string, variant?: 'primary' | 'secondary') => (
    <Button size="sm" variant={variant} onClick={reviewModalVisibility.show}>
      {label}
    </Button>
  );

  const getButtonLabel = (
    baseLabel: string,
    pendingOn: TaskParty | null,
    currentRole: TaskParty
  ) => {
    if (pendingOn === TaskParty.CLIENT && currentRole !== TaskParty.CLIENT) {
      return `${baseLabel} on client's behalf`;
    }
    return baseLabel;
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const isClientTask = task.pendingOn === TaskParty.CLIENT;
      const variant =
        isClientTask && state.role !== TaskParty.CLIENT
          ? 'secondary'
          : undefined;

      const stateMap: Record<
        string,
        Record<string, { actions: React.ReactNode; label: React.ReactNode }>
      > = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions:
              isClientTask && isCurrentTask ? startButton('Start') : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions:
              isClientTask && isCurrentTask
                ? startButton('Continue', 'secondary')
                : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: startButton('View', 'secondary'),
            label: (
              <ServiceRequestTaskLabel
                label={completedLabel}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: isCurrentTask
              ? startButton(
                  getButtonLabel('Start', task.pendingOn, state.role),
                  variant
                )
              : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: isCurrentTask
              ? startButton(
                  getButtonLabel('Continue', task.pendingOn, state.role),
                  'secondary'
                )
              : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: startButton('View', 'secondary'),
            label: (
              <ServiceRequestTaskLabel
                label={completedLabel}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.ADMIN]: {
          [TaskStatus.TO_DO]: {
            actions: isCurrentTask
              ? startButton(
                  getButtonLabel('Start', task.pendingOn, state.role),
                  variant
                )
              : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: isCurrentTask
              ? startButton(
                  getButtonLabel('Continue', task.pendingOn, state.role),
                  'secondary'
                )
              : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: startButton('View', 'secondary'),
            label: (
              <ServiceRequestTaskLabel
                label={completedLabel}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <HumanReviewModal
        isOpen={reviewModalVisibility.value}
        onClose={reviewModalVisibility.off}
        title={stepDefinition.name || task.title}
        description={stepDefinition.description}
        task={task}
        context={context}
        outputSchema={stepDefinition.outputSchema}
      />
    </div>
  );
};
